import { useEffect, useMemo, useState, useTransition } from "react";
import {
  AlignLeft,
  ArrowDown,
  ArrowUp,
  Calendar,
  Check,
  ChevronDown,
  CopyPlus,
  DollarSign,
  GripVertical,
  Hash,
  Link2,
  List,
  Loader2,
  Plus,
  RotateCcw,
  Save,
  Star,
  Text,
  ToggleLeft,
  Trash2,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { cn } from "../lib/utils";
import {
  AUDIENCE_LABELS,
  EOD_FORM_AUDIENCES,
  cloneQuestion,
  createBaseEodTemplate,
  createBaseEodTemplateRecord,
  createBlankEodTemplate,
  createBlankQuestion,
  sanitizeTemplate,
  slugifyQuestionKey,
  type EodFormAudience,
  type EodFormQuestion,
  type EodFormTemplate,
  type EodFormTemplateRecord,
  type EodQuestionType,
} from "../lib/eod-form-builder";

// ─── Type config ────────────────────────────────────────────────────────────

const TYPE_META: Record<
  EodQuestionType,
  { icon: React.ElementType<{ className?: string }>; label: string }
> = {
  short_text: { icon: Text, label: "Short answer" },
  long_text: { icon: AlignLeft, label: "Paragraph" },
  number: { icon: Hash, label: "Number" },
  currency: { icon: DollarSign, label: "Currency" },
  yes_no: { icon: ToggleLeft, label: "Yes / No" },
  rating: { icon: Star, label: "Rating" },
  select: { icon: List, label: "Dropdown" },
  date: { icon: Calendar, label: "Date" },
};

const ALL_TYPES = Object.keys(TYPE_META) as EodQuestionType[];

// ─── Helpers ────────────────────────────────────────────────────────────────

function buildTemplateRecord(
  input: Partial<EodFormTemplateRecord> | undefined,
): EodFormTemplateRecord {
  const base = createBaseEodTemplateRecord();
  return {
    setter: sanitizeTemplate("setter", input?.setter ?? base.setter),
    closer: sanitizeTemplate("closer", input?.closer ?? base.closer),
  };
}

// ─── Inline question preview (collapsed state) ─────────────────────────────

function QuestionPreview({ question }: { question: EodFormQuestion }) {
  if (question.type === "long_text")
    return <div className="mt-2 h-8 rounded border-b border-border/50" />;
  if (question.type === "short_text")
    return <div className="mt-2 h-8 w-1/2 rounded border-b border-border/50" />;
  if (question.type === "number" || question.type === "currency")
    return <div className="mt-2 h-8 w-32 rounded border-b border-border/50" />;
  if (question.type === "date")
    return (
      <div className="mt-2 flex h-8 w-40 items-center rounded border-b border-border/50 px-1 text-xs text-muted-foreground">
        <Calendar className="mr-2 h-3.5 w-3.5" /> Month, day, year
      </div>
    );
  if (question.type === "yes_no")
    return (
      <div className="mt-3 flex gap-3">
        <span className="rounded-full border border-border px-4 py-1.5 text-xs text-muted-foreground">Yes</span>
        <span className="rounded-full border border-border px-4 py-1.5 text-xs text-muted-foreground">No</span>
      </div>
    );
  if (question.type === "rating")
    return (
      <div className="mt-3 flex gap-1.5">
        {Array.from({ length: question.scaleMax }, (_, i) => (
          <span
            key={i}
            className="flex h-7 w-7 items-center justify-center rounded-full border border-border text-[11px] text-muted-foreground"
          >
            {i + 1}
          </span>
        ))}
      </div>
    );
  if (question.type === "select")
    return (
      <div className="mt-3 space-y-2">
        {question.options.slice(0, 3).map((opt, i) => (
          <div key={i} className="flex items-center gap-2.5 text-sm text-muted-foreground">
            <span className="h-4 w-4 rounded-full border border-border" />
            {opt}
          </div>
        ))}
        {question.options.length > 3 && (
          <p className="pl-6.5 text-xs text-muted-foreground/60">
            +{question.options.length - 3} more
          </p>
        )}
      </div>
    );
  return null;
}

// ─── Question card (expanded = editing, collapsed = preview) ────────────────

interface QuestionCardProps {
  question: EodFormQuestion;
  index: number;
  total: number;
  isSelected: boolean;
  onSelect: () => void;
  onUpdate: (id: string, updater: (q: EodFormQuestion) => EodFormQuestion) => void;
  onDuplicate: (id: string) => void;
  onRemove: (id: string) => void;
  onMove: (id: string, dir: "up" | "down") => void;
}

function QuestionCard({
  question,
  index,
  total,
  isSelected,
  onSelect,
  onUpdate,
  onDuplicate,
  onRemove,
  onMove,
}: QuestionCardProps) {
  const isBaseQuestion = question.source === "template";
  const needsPlaceholder =
    question.type !== "yes_no" && question.type !== "rating" && question.type !== "date";

  return (
    <div
      onClick={() => { if (!isSelected) onSelect(); }}
      className={cn(
        "group relative rounded-lg border bg-card transition-shadow duration-200",
        isSelected
          ? "border-[var(--brand-primary)]/50 shadow-md ring-1 ring-[var(--brand-primary)]/20"
          : "border-border/60 shadow-sm hover:shadow-md cursor-pointer",
      )}
    >
      {/* Left accent bar */}
      {isSelected && (
        <div className="absolute left-0 top-0 h-full w-1 rounded-l-lg bg-gradient-to-b from-[var(--brand-gradient-start)] to-[var(--brand-gradient-end)]" />
      )}

      <div className={cn("p-5", isSelected && "pl-6")}>
        {/* ── Selected / Editing state ── */}
        {isSelected ? (
          <div className="space-y-5">
            {/* Top row: label + type selector */}
            <div className="flex items-start gap-3">
              <div className="min-w-0 flex-1 space-y-1">
                <Input
                  value={question.label}
                  onChange={(e) => {
                    const nextLabel = e.target.value;
                    onUpdate(question.id, (cur) => {
                      const currentSlug = slugifyQuestionKey(cur.label);
                      const syncKey = !cur.key || cur.key === currentSlug;
                      return {
                        ...cur,
                        label: nextLabel,
                        key: syncKey ? slugifyQuestionKey(nextLabel) : cur.key,
                      };
                    });
                  }}
                  placeholder="Question"
                  className="border-0 border-b border-border/60 rounded-none bg-transparent px-0 text-base font-medium shadow-none focus-visible:border-[var(--brand-primary)] focus-visible:ring-0"
                />
              </div>
              <Select
                value={question.type}
                disabled={isBaseQuestion}
                onValueChange={(val) =>
                  onUpdate(question.id, (cur) => ({
                    ...cur,
                    type: val as EodQuestionType,
                    options:
                      val === "select"
                        ? cur.options.length > 0
                          ? cur.options
                          : ["Option 1", "Option 2"]
                        : [],
                    placeholder:
                      val === "long_text"
                        ? cur.placeholder || "Type your answer"
                        : cur.placeholder,
                  }))
                }
              >
                <SelectTrigger className="h-9 w-[160px] shrink-0 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ALL_TYPES.map((type) => {
                    const meta = TYPE_META[type];
                    const Icon = meta.icon;
                    return (
                      <SelectItem key={type} value={type}>
                        <Icon className="h-4 w-4 text-muted-foreground" />
                        {meta.label}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>

            {/* Description */}
            <Input
              value={question.description}
              onChange={(e) =>
                onUpdate(question.id, (cur) => ({ ...cur, description: e.target.value }))
              }
              placeholder="Description (optional)"
              className="border-0 border-b border-border/40 rounded-none bg-transparent px-0 text-sm text-muted-foreground shadow-none focus-visible:border-[var(--brand-primary)] focus-visible:ring-0"
            />

            {/* Type-specific editing */}
            {question.type === "select" && (
              <div className="space-y-2 pl-1">
                {question.options.map((opt, i) => (
                  <div key={i} className="flex items-center gap-2.5">
                    <span className="h-4 w-4 shrink-0 rounded-full border border-border" />
                    <Input
                      value={opt}
                      onChange={(e) => {
                        const newOpts = [...question.options];
                        newOpts[i] = e.target.value;
                        onUpdate(question.id, (cur) => ({ ...cur, options: newOpts }));
                      }}
                      className="h-8 border-0 border-b border-border/40 rounded-none bg-transparent px-0 text-sm shadow-none focus-visible:border-[var(--brand-primary)] focus-visible:ring-0"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        const newOpts = question.options.filter((_, j) => j !== i);
                        onUpdate(question.id, (cur) => ({ ...cur, options: newOpts }));
                      }}
                      className="shrink-0 rounded p-1 text-muted-foreground/50 hover:bg-muted hover:text-foreground"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() =>
                    onUpdate(question.id, (cur) => ({
                      ...cur,
                      options: [...cur.options, `Option ${cur.options.length + 1}`],
                    }))
                  }
                  className="flex items-center gap-2.5 text-sm text-muted-foreground hover:text-foreground"
                >
                  <span className="h-4 w-4 shrink-0 rounded-full border border-dashed border-border" />
                  Add option
                </button>
              </div>
            )}

            {question.type === "rating" && (
              <div className="flex items-center gap-3">
                <Label className="text-xs text-muted-foreground">Max rating</Label>
                <Input
                  type="number"
                  min={3}
                  max={10}
                  value={question.scaleMax}
                  onChange={(e) => {
                    const n = Number(e.target.value);
                    onUpdate(question.id, (cur) => ({
                      ...cur,
                      scaleMax: Number.isFinite(n) ? Math.max(3, Math.min(10, n)) : cur.scaleMax,
                    }));
                  }}
                  className="h-8 w-20 text-sm"
                />
              </div>
            )}

            {needsPlaceholder && (
              <Input
                value={question.placeholder}
                onChange={(e) =>
                  onUpdate(question.id, (cur) => ({ ...cur, placeholder: e.target.value }))
                }
                placeholder="Placeholder text (optional)"
                className="border-0 border-b border-border/40 rounded-none bg-transparent px-0 text-sm text-muted-foreground shadow-none focus-visible:border-[var(--brand-primary)] focus-visible:ring-0"
              />
            )}

            {/* Advanced fields row */}
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Section</Label>
                <Input
                  value={question.section}
                  onChange={(e) =>
                    onUpdate(question.id, (cur) => ({ ...cur, section: e.target.value }))
                  }
                  placeholder="General"
                  className="h-8 text-sm"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Field key</Label>
                <Input
                  value={question.key}
                  onChange={(e) => {
                    if (isBaseQuestion) return;
                    onUpdate(question.id, (cur) => ({
                      ...cur,
                      key: slugifyQuestionKey(e.target.value),
                    }));
                  }}
                  placeholder="field_key"
                  className="h-8 font-mono text-sm"
                  readOnly={isBaseQuestion}
                />
                {isBaseQuestion && (
                  <p className="text-[11px] text-muted-foreground">
                    Base EOD fields use locked keys so submissions always map into analytics and sheets.
                  </p>
                )}
              </div>
            </div>

            {/* Bottom toolbar */}
            <div className="flex items-center justify-between border-t border-border/40 pt-4">
              <div className="flex items-center gap-1">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0"
                  onClick={() => onMove(question.id, "up")}
                  disabled={index === 0}
                >
                  <ArrowUp className="h-4 w-4" />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0"
                  onClick={() => onMove(question.id, "down")}
                  disabled={index === total - 1}
                >
                  <ArrowDown className="h-4 w-4" />
                </Button>
                <div className="mx-1 h-5 w-px bg-border/60" />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0"
                  onClick={() => onDuplicate(question.id)}
                >
                  <CopyPlus className="h-4 w-4" />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0 text-red-500 hover:bg-red-500/10 hover:text-red-600"
                  onClick={() => onRemove(question.id)}
                  disabled={isBaseQuestion}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>

              {/* Required toggle */}
              <div className="flex items-center gap-2.5">
                <span className="text-sm text-muted-foreground">Required</span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={question.required}
                  onClick={() => {
                    if (isBaseQuestion) return;
                    onUpdate(question.id, (cur) => ({ ...cur, required: !cur.required }));
                  }}
                  className={cn(
                    "relative h-5 w-9 rounded-full transition-colors duration-200",
                    question.required
                      ? "bg-[var(--brand-primary)]"
                      : "bg-muted-foreground/25",
                    isBaseQuestion && "cursor-not-allowed opacity-60",
                  )}
                >
                  <span
                    className={cn(
                      "absolute top-0.5 left-0.5 h-4 w-4 rounded-full bg-white shadow-sm transition-transform duration-200",
                      question.required && "translate-x-4",
                    )}
                  />
                </button>
              </div>
            </div>
          </div>
        ) : (
          /* ── Collapsed / Preview state ── */
          <div>
            <div className="flex items-start gap-3">
              <GripVertical className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground/30 opacity-0 transition-opacity group-hover:opacity-100" />
              <div className="min-w-0 flex-1">
                <p className="text-sm text-foreground">
                  {question.label || (
                    <span className="italic text-muted-foreground">Untitled question</span>
                  )}
                  {question.required && (
                    <span className="ml-1 text-[var(--brand-primary)]">*</span>
                  )}
                </p>
                {question.description && (
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    {question.description}
                  </p>
                )}
                <QuestionPreview question={question} />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Add question floating bar ──────────────────────────────────────────────

function AddQuestionBar({ onAdd }: { onAdd: (type: EodQuestionType) => void }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="flex items-center justify-center py-2">
      <div className="relative">
        <button
          type="button"
          onClick={() => setOpen(!open)}
          className={cn(
            "flex items-center gap-2 rounded-full border border-border/60 bg-card px-4 py-2 text-sm font-medium text-muted-foreground shadow-sm transition-all hover:border-[var(--brand-primary)]/40 hover:text-[var(--brand-primary)] hover:shadow-md",
            open && "border-[var(--brand-primary)]/40 text-[var(--brand-primary)] shadow-md",
          )}
        >
          <Plus className="h-4 w-4" />
          Add question
          <ChevronDown className={cn("h-3.5 w-3.5 transition-transform", open && "rotate-180")} />
        </button>

        {open && (
          <div className="absolute left-1/2 top-full z-20 mt-2 -translate-x-1/2 rounded-lg border border-border/60 bg-card p-1.5 shadow-lg">
            <div className="grid grid-cols-2 gap-1" style={{ minWidth: "280px" }}>
              {ALL_TYPES.map((type) => {
                const meta = TYPE_META[type];
                const Icon = meta.icon;
                return (
                  <button
                    key={type}
                    type="button"
                    onClick={() => {
                      onAdd(type);
                      setOpen(false);
                    }}
                    className="flex items-center gap-2.5 rounded-md px-3 py-2 text-sm text-foreground transition-colors hover:bg-muted"
                  >
                    <Icon className="h-4 w-4 text-muted-foreground" />
                    {meta.label}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Main EODBuilder ────────────────────────────────────────────────────────

export function EODBuilder() {
  const [activeAudience, setActiveAudience] = useState<EodFormAudience>("setter");
  const [draftTemplates, setDraftTemplates] = useState<EodFormTemplateRecord>(() =>
    createBaseEodTemplateRecord(),
  );
  const [savedTemplates, setSavedTemplates] = useState<EodFormTemplateRecord>(() =>
    createBaseEodTemplateRecord(),
  );
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [selectedQuestionId, setSelectedQuestionId] = useState<string | null>(null);
  const [copiedAudience, setCopiedAudience] = useState<EodFormAudience | null>(null);
  const [isSwitchingAudience, startTransition] = useTransition();

  // ── Load templates ──

  useEffect(() => {
    let cancelled = false;
    async function loadTemplates() {
      try {
        setIsLoading(true);
        setError(null);
        const response = await fetch("/api/eod-form-templates");
        const result = await response.json();
        if (!response.ok || !result.success)
          throw new Error(result.error || "Failed to load EOD templates");
        const templates = buildTemplateRecord(result.data);
        if (cancelled) return;
        setDraftTemplates(templates);
        setSavedTemplates(templates);
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : "Failed to load EOD templates");
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }
    loadTemplates();
    return () => {
      cancelled = true;
    };
  }, []);

  // ── Derived state ──

  const activeTemplate = draftTemplates[activeAudience];
  const savedTemplate = savedTemplates[activeAudience];

  const isDirty = useMemo(
    () => JSON.stringify(activeTemplate) !== JSON.stringify(savedTemplate),
    [activeTemplate, savedTemplate],
  );

  // ── Actions ──

  const updateTemplate = (
    audience: EodFormAudience,
    updater: (t: EodFormTemplate) => EodFormTemplate,
  ) => {
    setDraftTemplates((cur) => ({ ...cur, [audience]: updater(cur[audience]) }));
  };

  const updateQuestion = (
    questionId: string,
    updater: (q: EodFormQuestion) => EodFormQuestion,
  ) => {
    updateTemplate(activeAudience, (t) => ({
      ...t,
      questions: t.questions.map((q) => (q.id === questionId ? updater(q) : q)),
    }));
  };

  const moveQuestion = (questionId: string, direction: "up" | "down") => {
    updateTemplate(activeAudience, (t) => {
      const idx = t.questions.findIndex((q) => q.id === questionId);
      if (idx === -1) return t;
      const targetIdx = direction === "up" ? idx - 1 : idx + 1;
      if (targetIdx < 0 || targetIdx >= t.questions.length) return t;
      const next = [...t.questions];
      const moved = next.splice(idx, 1)[0]!;
      next.splice(targetIdx, 0, moved);
      return { ...t, questions: next };
    });
  };

  const duplicateQuestion = (questionId: string) => {
    updateTemplate(activeAudience, (t) => {
      const idx = t.questions.findIndex((q) => q.id === questionId);
      if (idx === -1) return t;
      const orig = t.questions[idx]!;
      const seed = createBlankQuestion(activeAudience, orig.type);
      const dupe = cloneQuestion({
        ...orig,
        id: seed.id,
        key: slugifyQuestionKey(`${orig.key}_copy`),
        label: `${orig.label} (copy)`,
        source: "custom" as const,
      });
      const next = [...t.questions];
      next.splice(idx + 1, 0, dupe);
      return { ...t, questions: next };
    });
  };

  const removeQuestion = (questionId: string) => {
    if (selectedQuestionId === questionId) setSelectedQuestionId(null);
    updateTemplate(activeAudience, (t) => ({
      ...t,
      questions: t.questions.filter((q) => q.id !== questionId),
    }));
  };

  const addQuestion = (type: EodQuestionType) => {
    const newQ = createBlankQuestion(activeAudience, type);
    setDraftTemplates((cur) => ({
      ...cur,
      [activeAudience]: {
        ...cur[activeAudience],
        questions: [...cur[activeAudience].questions, newQ],
      },
    }));
    setSelectedQuestionId(newQ.id);
  };

  const copyFormLink = async (audience: EodFormAudience) => {
    const url = `${window.location.origin}/eod/${audience}`;
    await navigator.clipboard.writeText(url);
    setCopiedAudience(audience);
    setTimeout(() => setCopiedAudience(null), 2000);
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      setError(null);
      setNotice(null);
      const response = await fetch("/api/eod-form-templates", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(activeTemplate),
      });
      const result = await response.json();
      if (!response.ok || !result.success)
        throw new Error(result.error || "Failed to save template");
      const sanitized = sanitizeTemplate(activeAudience, result.data);
      setDraftTemplates((cur) => ({ ...cur, [activeAudience]: sanitized }));
      setSavedTemplates((cur) => ({ ...cur, [activeAudience]: sanitized }));
      setNotice(`${AUDIENCE_LABELS[activeAudience]} template saved`);
      setTimeout(() => setNotice(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save template");
    } finally {
      setIsSaving(false);
    }
  };

  const resetToStarter = () => {
    startTransition(() => {
      updateTemplate(activeAudience, () => createBaseEodTemplate(activeAudience));
      setSelectedQuestionId(null);
    });
  };

  const startBlank = () => {
    startTransition(() => {
      updateTemplate(activeAudience, () => createBlankEodTemplate(activeAudience));
      setSelectedQuestionId(null);
    });
  };

  const handleAudienceSwitch = (audience: EodFormAudience) => {
    startTransition(() => {
      setActiveAudience(audience);
      setSelectedQuestionId(null);
    });
  };

  // ── Loading ──

  if (isLoading) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-10">
        <div className="space-y-4">
          <div className="h-32 animate-pulse rounded-lg bg-muted/40" />
          <div className="h-24 animate-pulse rounded-lg bg-muted/30" />
          <div className="h-24 animate-pulse rounded-lg bg-muted/20" />
          <div className="h-24 animate-pulse rounded-lg bg-muted/20" />
        </div>
      </div>
    );
  }

  // ── Render ──

  return (
    <div className="min-h-screen pb-28">
      {/* ── Sticky top bar ── */}
      <div className="sticky top-0 z-30 border-b border-border/60 bg-background/95 backdrop-blur-sm">
        <div className="mx-auto flex max-w-2xl items-center justify-between gap-3 px-4 py-3">
          {/* Audience tabs */}
          <div className="flex items-center gap-1 rounded-lg bg-muted/50 p-1">
            {EOD_FORM_AUDIENCES.map((audience) => {
              const isActive = audience === activeAudience;
              return (
                <button
                  key={audience}
                  type="button"
                  onClick={() => handleAudienceSwitch(audience)}
                  disabled={isSwitchingAudience}
                  className={cn(
                    "flex items-center gap-1.5 rounded-md px-3.5 py-1.5 text-sm font-medium transition-all",
                    isActive
                      ? "bg-card text-foreground shadow-sm"
                      : "text-muted-foreground hover:text-foreground",
                  )}
                >
                  {AUDIENCE_LABELS[audience]}
                  <span
                    className={cn(
                      "rounded-full px-1.5 py-0.5 text-[10px] font-semibold tabular-nums",
                      isActive ? "bg-[var(--brand-primary)]/10 text-[var(--brand-primary)]" : "bg-muted text-muted-foreground",
                    )}
                  >
                    {draftTemplates[audience].questions.length}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Right side: share + save */}
          <div className="flex items-center gap-2">
            {/* Share link */}
            <button
              type="button"
              onClick={() => copyFormLink(activeAudience)}
              className="flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            >
              {copiedAudience === activeAudience ? (
                <Check className="h-3.5 w-3.5 text-emerald-500" />
              ) : (
                <Link2 className="h-3.5 w-3.5" />
              )}
              {copiedAudience === activeAudience ? "Copied!" : "Share"}
            </button>

            <Button
              type="button"
              size="sm"
              onClick={handleSave}
              disabled={!isDirty || isSaving}
              className="gap-1.5"
            >
              {isSaving ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Save className="h-3.5 w-3.5" />
              )}
              {isDirty ? "Save" : "Saved"}
            </Button>
          </div>
        </div>
      </div>

      {/* ── Notices ── */}
      <div className="mx-auto max-w-2xl px-4">
        {error && (
          <div className="mt-4 rounded-lg border border-red-500/25 bg-red-500/5 px-4 py-3 text-sm text-red-600 dark:text-red-400">
            {error}
          </div>
        )}
        {notice && (
          <div className="mt-4 rounded-lg border border-emerald-500/25 bg-emerald-500/5 px-4 py-3 text-sm text-emerald-700 dark:text-emerald-400">
            {notice}
          </div>
        )}
      </div>

      {/* ── Main content ── */}
      <div className="mx-auto max-w-2xl space-y-3 px-4 pt-6">
        {/* ── Title card (Google Forms style with gradient top) ── */}
        <div className="overflow-hidden rounded-lg border border-border/60 bg-card shadow-sm">
          <div className="h-2.5 bg-gradient-to-r from-[var(--brand-gradient-start)] to-[var(--brand-gradient-end)]" />
          <div className="space-y-3 p-5">
            <input
              value={activeTemplate.title}
              onChange={(e) =>
                updateTemplate(activeAudience, (t) => ({ ...t, title: e.target.value }))
              }
              placeholder="Form title"
              className="w-full border-0 border-b border-transparent bg-transparent text-2xl font-semibold text-foreground outline-none placeholder:text-muted-foreground/50 focus:border-[var(--brand-primary)]"
            />
            <input
              value={activeTemplate.description}
              onChange={(e) =>
                updateTemplate(activeAudience, (t) => ({ ...t, description: e.target.value }))
              }
              placeholder="Form description"
              className="w-full border-0 border-b border-transparent bg-transparent text-sm text-muted-foreground outline-none placeholder:text-muted-foreground/40 focus:border-[var(--brand-primary)]"
            />
          </div>
        </div>

        {/* ── Questions ── */}
        {activeTemplate.questions.length === 0 ? (
          /* Empty state */
          <div className="rounded-lg border border-dashed border-border bg-card px-6 py-14 text-center shadow-sm">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-muted/50">
              <Plus className="h-6 w-6 text-muted-foreground" />
            </div>
            <p className="text-base font-medium text-foreground">No questions yet</p>
            <p className="mt-1.5 text-sm text-muted-foreground">
              Start from a template or build from scratch
            </p>
            <div className="mt-6 flex items-center justify-center gap-3">
              <Button type="button" variant="outline" size="sm" onClick={resetToStarter} className="gap-1.5">
                <RotateCcw className="h-3.5 w-3.5" />
                Use starter template
              </Button>
              <Button type="button" size="sm" onClick={() => addQuestion("short_text")} className="gap-1.5">
                <Plus className="h-3.5 w-3.5" />
                Add first question
              </Button>
            </div>
          </div>
        ) : (
          <>
            {activeTemplate.questions.map((question, index) => (
              <QuestionCard
                key={question.id}
                question={question}
                index={index}
                total={activeTemplate.questions.length}
                isSelected={selectedQuestionId === question.id}
                onSelect={() => setSelectedQuestionId(question.id)}
                onUpdate={updateQuestion}
                onDuplicate={duplicateQuestion}
                onRemove={removeQuestion}
                onMove={moveQuestion}
              />
            ))}

            <AddQuestionBar onAdd={addQuestion} />

            {/* Reset options */}
            <div className="flex items-center justify-center gap-3 pb-4 pt-2">
              <button
                type="button"
                onClick={resetToStarter}
                className="flex items-center gap-1.5 text-xs text-muted-foreground/60 transition-colors hover:text-muted-foreground"
              >
                <RotateCcw className="h-3 w-3" />
                Reset to starter
              </button>
              <span className="text-muted-foreground/30">·</span>
              <button
                type="button"
                onClick={startBlank}
                className="flex items-center gap-1.5 text-xs text-muted-foreground/60 transition-colors hover:text-muted-foreground"
              >
                Clear all
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
